import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Calendar, Play, X } from 'lucide-react';

const TeacherVideoModal = ({ teacher, isOpen, onClose, onSchedule }) => {
  const { language } = useLanguage();

  if (!teacher) return null;

  const content = {
    en: {
      introduction: "Introduction Video",
      about: "About",
      schedule: "Schedule Assessment with",
      close: "Close"
    },
    ar: {
      introduction: "فيديو التعريف",
      about: "حول",
      schedule: "حجز تقييم مع",
      close: "إغلاق"
    }
  };

  const t = content[language];

  // Extract video ID from YouTube URL for embed
  const getEmbedUrl = (url) => {
    if (!url) return null;
    // Handle both youtube.com and youtu.be URLs
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  const handleScheduleClick = () => {
    // Use the internal booking modal
    if (onSchedule) {
      onSchedule(teacher);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Play className="w-6 h-6 text-teal-600" />
              <span>{teacher.name}</span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </DialogTitle>
          <p className="text-teal-600 font-medium text-lg">
            {teacher.title?.[language] || (language === 'en' ? 'English Teacher' : 'معلم لغة إنجليزية')}
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Video Player */}
          {teacher.videoUrl || teacher.intro_video_url ? (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl"
                src={getEmbedUrl(teacher.videoUrl || teacher.intro_video_url)}
                title={`${teacher.name} Introduction`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          ) : (
            <div className="bg-gray-100 rounded-xl p-12 text-center">
              <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {language === 'en' ? 'No introduction video available' : 'لا يوجد فيديو تعريفي متاح'}
              </p>
            </div>
          )}

          {/* Teacher Bio */}
          <div className="bg-slate-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              {t.about} {teacher.name}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {teacher.bio?.[language] || teacher.bio || (language === 'en' ? 'Professional English teacher with years of experience.' : 'معلم لغة إنجليزية محترف ذو خبرة سنوات.')}
            </p>
          </div>

          {/* Schedule CTA */}
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">
              {t.schedule} {teacher.name}?
            </h3>
            <p className="text-teal-50 mb-6">
              {language === 'en' 
                ? 'Book a free assessment session to discuss your English learning goals.' 
                : 'احجز جلسة تقييم مجانية لمناقشة أهدافك في تعلم اللغة الإنجليزية.'}
            </p>
            <Button
              onClick={handleScheduleClick}
              className="bg-white text-teal-600 hover:bg-teal-50 text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="schedule-assessment-btn"
            >
              <Calendar className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              {language === 'en' ? 'Schedule Free Assessment' : 'حجز تقييم مجاني'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeacherVideoModal;
